import { LoadingArea } from '@consigli/facade';
import {
  useDocumentPackageSummary,
  useFindings,
  useGetDashboardDataQuery,
  usePackageByTypeAndPackageId,
  usePackageId,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import {
  ConflictCategory,
  DocumentPackageSummarySection,
  DocumentPackageType,
  Finding,
} from '@consigli/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { ServiceIdIndexValue } from '@/routes';
import { useDemoMode } from '@/util/demo-mode';

import { SummaryAccordionItem } from './summary-accordion-item';
import { SummaryDescription } from './summary-description';
import { SummarySection } from './summary-section';
import { CATEGORY_SORT_ORDER, FlatConflictItem, flattenCategories, sumCounts } from './utils';

export const TenderDocumentsSummaryPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const serviceName = useServiceName();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );
  const isPublished = pkg ? pkg.published : false;

  const { data: dashboardData, isLoading: isDashboardLoading } = useGetDashboardDataQuery({
    projectId,
    packageId,
  });
  const { summary, isLoading: isSummaryLoading } = useDocumentPackageSummary(projectId, packageId);
  // page: 'all' is intentional — findings are resolved client-side by ID so
  // that each summary section can display its referenced finding texts inline
  // without a per-section network request.
  const { findings, isLoading: isFindingsLoading } = useFindings({
    projectId,
    packageId,
    page: 'all',
  });

  const isLoading = isDashboardLoading || isSummaryLoading || isFindingsLoading;

  const waitingForAnalysis = !adminFeaturesEnabled && !isPublished;

  const findingsById = useMemo<Map<number, Finding>>(() => {
    return new Map(findings.map((f) => [f.id, f]));
  }, [findings]);

  const sectionsByCategory = useMemo<Map<string, DocumentPackageSummarySection[]>>(() => {
    const map = new Map<string, DocumentPackageSummarySection[]>();
    if (!summary) return map;
    for (const section of summary.sections) {
      const existing = map.get(section.categoryType) ?? [];
      map.set(section.categoryType, [...existing, section]);
    }
    return map;
  }, [summary]);

  const allConflictEntries = useMemo(() => {
    if (!dashboardData?.conflictTypesCount) return [];
    return Object.entries(dashboardData.conflictTypesCount) as [
      string,
      (typeof dashboardData.conflictTypesCount)[string],
    ][];
  }, [dashboardData]);

  const riskItems = useMemo(() => {
    // INSIGHT is rendered separately via insightItems; sort the rest by a fixed
    // display order so risk categories always appear in a predictable sequence.
    const categories = allConflictEntries
      .filter(([key]) => key !== ConflictCategory.INSIGHT)
      .sort(
        ([a], [b]) =>
          (CATEGORY_SORT_ORDER[a as ConflictCategory] ?? 99) -
          (CATEGORY_SORT_ORDER[b as ConflictCategory] ?? 99),
      );
    return flattenCategories(categories);
  }, [allConflictEntries]);

  const insightItems = useMemo(() => {
    const categories = allConflictEntries.filter(([key]) => key === ConflictCategory.INSIGHT);
    return flattenCategories(categories);
  }, [allConflictEntries]);

  const accordionItems = (items: FlatConflictItem[]) =>
    items.map((item) => (
      <SummaryAccordionItem
        key={item.typeId}
        title={t([
          `dashboard.conflict-title.${item.typeId}`,
          'dashboard.conflict-title.__fallback',
        ])}
        count={item.count}
        findingsHref={`../findings?conflictCategory=${item.category}&conflictType=${item.typeId}`}
        sections={sectionsByCategory.get(item.typeId) ?? []}
        findingsById={findingsById}
      />
    ));

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={waitingForAnalysis}>
        {waitingForAnalysis ? (
          <WaitingForAnalysis />
        ) : isLoading ? (
          <LoadingArea />
        ) : (
          <div className="bg-day-light-3 min-h-full px-40 py-10">
            <h1 className="text-3xl font-bold text-day-neutral-dark mb-3 tracking-tight">
              {t('summary.title')}
            </h1>
            <p className="text-sm text-day-neutral-subtle mb-8">
              {t('summary.subtitle', { defaultValue: '' })}
            </p>

            <div className="bg-day-light-5 rounded-xl border border-day-light-2 shadow-(--shadow-card) px-6 py-5 text-sm text-day-neutral-dark leading-loose mb-10">
              {summary?.description ? (
                <SummaryDescription description={summary.description} />
              ) : (
                <span className="text-day-neutral-subtle italic">{t('summary.placeholder')}</span>
              )}
            </div>

            <SummarySection title={t('summary.risk-findings')} count={sumCounts(riskItems)}>
              {accordionItems(riskItems)}
            </SummarySection>

            <SummarySection title={t('summary.insights')} count={sumCounts(insightItems)}>
              {accordionItems(insightItems)}
            </SummarySection>
          </div>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
